// 西语打印：core-api /api/print/<kind>/<id> 统一入口可返回旧版 HTML 或外部模板服务生成的 PDF。
// 该接口需要 Authorization 头，浏览器普通 <a>/window.open 不会自动带上，
// 因此先用带 token 的 fetch 拉取 HTML，再在已打开的弹窗中渲染并触发打印 / 另存 PDF。
import { getToken } from './api';
import type { PrintKind } from './types';

const KIND_LABEL: Record<PrintKind, string> = {
  quotation: 'Cotización',
  contract: 'Contrato',
  delivery: 'Remito',
  inspection: 'Informe de calidad',
  'asset-return': 'Devolución de pieza',
};

export interface PrintResult {
  ok: boolean;
  message?: string;
}

// 用 Blob URL 把 HTML 加载进弹窗：比 document.write 更稳，可避免 noopener / 异步写入竞态导致的白屏。
function renderInWindow(win: Window, html: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  let printed = false;
  const doPrint = () => {
    if (printed || win.closed) return;
    // 文档未就绪绝不打印：否则打印预览拿到半成品布局（症状：预览变形、另存 PDF 却正常）。
    if (win.document.readyState !== 'complete') return;
    printed = true;
    try { win.focus(); win.print(); } catch { /* ignore */ }
  };
  // window 级 onload 在导航到 blob 后仍会触发；document 级 listener 不会转移到新文档，
  // 因此历史上只靠 800ms 定时器兜底——文档没加载完就 print，预览即半成品。
  win.onload = doPrint;
  win.location.href = url;
  // 兜底轮询：个别环境 onload 不触发时，就绪即补打（只打一次）；10s 仍未就绪也强制打印，避免静默无反应。
  const poll = setInterval(doPrint, 250);
  setTimeout(() => clearInterval(poll), 10000);
  setTimeout(() => {
    if (printed || win.closed) return;
    printed = true;
    try { win.focus(); win.print(); } catch { /* ignore */ }
  }, 10000);
  // blob URL 保留到打印对话框结束后：提前 revoke 会让预览在改动纸张/边距/缩放重新布局时撕裂。
  // 对话框关闭（保存或取消都会触发 afterprint）后回收 URL，并自动关掉弹窗——
  // 否则保存完桌面上残留一个打印页窗口，与主界面并排，观感上就像“界面变形”。
  win.onafterprint = () => {
    URL.revokeObjectURL(url);
    setTimeout(() => { try { win.close(); } catch { /* ignore */ } }, 300);
  };
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

function renderPdfInWindow(win: Window, pdf: Blob): void {
  const url = URL.createObjectURL(pdf);
  win.location.href = url;
  // PDF viewer may keep reading the object URL; revoke on a generous delay rather than immediately.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// 弹窗被拦截时的兜底：用临时 <a download> 直接下载 PDF，不依赖弹窗权限。
function downloadPdf(pdf: Blob, filename: string): void {
  const url = URL.createObjectURL(pdf);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// 后端 sendBinary 返回 inline; filename="单号.pdf"，取不到时退回 单据类型-id.pdf。
function pdfFilename(res: Response, kind: string, id: string): string {
  const m = /filename="([^"]+)"/i.exec(res.headers.get('content-disposition') || '');
  return m ? m[1] : `${kind}-${id}.pdf`;
}

export async function openAuthenticatedPrint(kind: PrintKind, id: string, opts?: { format?: 'pdf' }): Promise<PrintResult> {
  // 关键：window.open 必须在用户点击的同步上下文里调用，否则弹窗拦截器会拦截
  // （表现为"浏览器拦截弹窗 / 空白窗口"）。因此先开空白窗口拿到用户手势，再异步拉取 HTML 写入。
  // 若弹窗仍被拦截（嵌入浏览器 / 严格拦截设置），不再直接失败：拉取成功后走直接下载 PDF 兜底。
  const win = window.open('', '_blank', 'width=980,height=1200');
  const token = getToken();
  // format=pdf：core-api 在 puppeteer 可用时返回真实 PDF；不可用时回落 HTML，两种都由下方统一处理。
  const qs = opts?.format === 'pdf' ? '?format=pdf' : '';
  let res: Response;
  try {
    res = await fetch(`/api/print/${kind}/${encodeURIComponent(id)}${qs}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    win?.close();
    return { ok: false, message: '无法连接核心服务，请确认 core-api（18890）已启动' };
  }
  if (!res.ok) {
    win?.close();
    let msg = `打印失败 ${res.status}`;
    try {
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('json')) {
        const body = (await res.json()) as { error?: string };
        if (body?.error) msg = body.error;
      }
    } catch { /* ignore */ }
    return { ok: false, message: msg };
  }
  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  if (contentType.includes('application/pdf')) {
    const pdf = await res.blob();
    if (!pdf.size) {
      win?.close();
      return { ok: false, message: '打印服务返回了空 PDF。' };
    }
    if (win) {
      renderPdfInWindow(win, pdf);
      return { ok: true, message: `${KIND_LABEL[kind]} PDF 已在新窗口打开。` };
    }
    downloadPdf(pdf, pdfFilename(res, kind, id));
    return { ok: true, message: `浏览器拦截了新窗口，${KIND_LABEL[kind]} PDF 已改为直接下载。` };
  }
  const html = await res.text();
  // 防御：若返回的不是我们生成的打印页（例如 /api 未反代、回落到 SPA 空壳），直接报错而不是白屏。
  // 注意：Portal 版式（NOTA DE RECEPCION / REMITO X）与旧 Remito 版式标识不同，任一命中即可。
  // 正式销售合同（ORDEN DE VENTA）按参考件版式不输出页脚，故按单据标题识别。
  const looksLikePrint = !!html && (
    html.includes('PINNACLE LASER ERP')
    || html.includes('NOTA DE RECEPCION')
    || html.includes('Documento NO VALIDO COMO FACTURA')
    || html.includes('DOCUMENTO NO VALIDO COMO FACTURA')
    || html.includes('Portal del Cliente')
    || html.includes('Pinnacle Laser')
    || html.includes('ORDEN DE VENTA')
    || html.includes('COTIZACIÓN')
    || html.includes('INSPECCIÓN')
  );
  if (!looksLikePrint) {
    win?.close();
    return { ok: false, message: '打印接口未返回有效内容，请检查 /api/print 代理与 core-api 状态。' };
  }
  if (win) {
    renderInWindow(win, html);
    return { ok: true, message: `${KIND_LABEL[kind]} 打印预览已在新窗口打开。` };
  }
  printViaHiddenIframe(html);
  return { ok: true, message: `浏览器拦截了新窗口，已直接调起${KIND_LABEL[kind]}打印。` };
}

// 弹窗被拦截时的兜底：把 HTML 打印页装入隐藏 iframe 并调起打印对话框，不依赖弹窗权限。
function printViaHiddenIframe(html: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  iframe.onload = () => {
    try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch { /* ignore */ }
    // 打印对话框关闭（保存或取消）后移除 iframe 并回收 URL，避免节点与 blob 残留。
    try {
      iframe.contentWindow?.addEventListener('afterprint', () => {
        URL.revokeObjectURL(url);
        iframe.remove();
      }, { once: true });
    } catch { /* ignore */ }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  iframe.src = url;
  document.body.appendChild(iframe);
}
